import type { AdminDict } from "@/lib/admin-i18n";

export type ChecklistState = {
  no_ids: boolean;
  no_names: boolean;
  warnings: boolean;
  facets: boolean;
  tone: boolean;
  language_register: boolean;
  confessor_anonymity: boolean;
};

export const EMPTY_CHECKLIST: ChecklistState = {
  no_ids: false, no_names: false, warnings: false, facets: false,
  tone: false, language_register: false, confessor_anonymity: false,
};

export function isChecklistComplete(c: ChecklistState): boolean {
  return Object.values(c).every(Boolean);
}

export function Checklist({
  t, value, onChange, isConfessor,
}: {
  t: AdminDict;
  value: ChecklistState;
  onChange: (next: ChecklistState) => void;
  isConfessor: boolean;
}) {
  const set = (k: keyof ChecklistState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [k]: e.target.checked });

  // Rule 7 only applies to confessor submissions; for victim stories we tick
  // it automatically so the gate doesn't block publication.
  const r7Disabled = !isConfessor;

  return (
    <aside
      className="rounded-sm border p-4 text-sm"
      style={{ borderColor: "var(--fog)", background: "color-mix(in oklab, var(--paper) 92%, var(--amber) 8%)" }}
    >
      <h3 className="font-display text-lg" style={{ color: "var(--ink)" }}>
        {t.checklist_title}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">{t.checklist_hint}</p>
      <ul className="mt-3 space-y-3">
        {([
          ["no_ids", t.rule_1],
          ["no_names", t.rule_2],
          ["warnings", t.rule_3],
          ["facets", t.rule_4],
          ["tone", t.rule_5],
        ] as const).map(([k, label]) => (
          <li key={k} className="flex gap-2">
            <input
              type="checkbox" checked={value[k]} onChange={set(k)}
              className="mt-1 size-4 accent-[color:var(--amber)]"
            />
            <span>{label}</span>
          </li>
        ))}

        <li className="flex gap-2">
          <input
            type="checkbox" checked={value.language_register} onChange={set("language_register")}
            className="mt-1 size-4 accent-[color:var(--amber)]"
          />
          <span>
            <span>{t.rule_6}</span>
            <span className="mt-1 block text-xs text-muted-foreground">{t.rule_6_a}</span>
            <span className="mt-1 block text-xs text-muted-foreground">{t.rule_6_b}</span>
          </span>
        </li>

        <li className={`flex gap-2 ${r7Disabled ? "opacity-50" : ""}`}>
          <input
            type="checkbox"
            checked={r7Disabled ? true : value.confessor_anonymity}
            disabled={r7Disabled}
            onChange={set("confessor_anonymity")}
            className="mt-1 size-4 accent-[color:var(--amber)]"
          />
          <span>{t.rule_7}</span>
        </li>
      </ul>
    </aside>
  );
}